<!-- Register new types in helpers/InAppNotificationService.ts in getRenderComponent(...) -->
<script lang="ts" setup>
  import { computed, onMounted, ref, toRefs } from 'vue'

  import type { EffortJsonldVueGlobalSearchDefaultReadSync, EmployeeNotificationJsonldDefaultRead } from '@/api'
  import { useGeneratedEffortStore } from '@/stores'
  import Menu from 'primevue/menu'
  import type { MenuItem } from 'primevue/menuitem'
  import { useRouter } from 'vue-router'
  import EffortLink from '@/elements/EffortLink.vue'
  import PageLoading from '@/components/PageLoading.vue'
  import ButtonDefault from '@/elements/ButtonDefault.vue'
  import { LocalNotificationBaseNonGeneric, useInAppNotificationService } from '@/services/InAppNotificationService.ts'

  interface Props {
    notification: EmployeeNotificationJsonldDefaultRead
  }

  interface Data {
    effort: number
    mode: 'RESPONSIBLE' | 'OWNER' | 'MEMBER'
  }

  const props = defineProps<Props>()
  const { notification } = toRefs(props)
  const notificationService = useInAppNotificationService()

  const r = useRouter()
  const loading = ref(true)
  const menu = ref<InstanceType<typeof Menu>>()

  const contextMenuItems = computed<MenuItem[] | undefined>(() => {
    if (!notification.value) return undefined

    const n = notification.value

    const cmdOpenObject: MenuItem = {
      label: 'Objekt öffnen',
      command: async () => {
        const route = r.resolve({
          name: 'effort_details',
          params: { id: n.data.effort }
        })
        window.open(route.href, '_blank')
      },
      icon: 'InternalLinkIcon'
    }

    const cmdMarkAsRead: MenuItem = {
      label: 'Als gelesen markieren',
      command: () => {
        void notificationService.setAsRead(n!, true)
      },
      icon: 'EyeCheckIcon'
    }

    const cmdMarkAsUnread: MenuItem = {
      label: 'Als ungelesen markieren',
      command: () => {
        void notificationService.setAsRead(n!, false)
      },
      icon: 'EyeOffIcon'
    }

    const cmdRemove: MenuItem = {
      label: 'Löschen',
      command: () => {
        notificationService.removeLocal(n as LocalNotificationBaseNonGeneric)
      },
      icon: 'DeleteIcon'
    }

    const actions: MenuItem[] = [cmdOpenObject]
    actions.push(n.read ? cmdMarkAsUnread : cmdMarkAsRead)
    if (n.read && (n as LocalNotificationBaseNonGeneric).deletable) actions.push(cmdRemove)
    return actions
  })


  const data = computed<Data>(() => notification.value.data as Data)
  const effort = ref<EffortJsonldVueGlobalSearchDefaultReadSync>()

  function toggleMenu(e: Event) {
    menu.value?.toggle(e)
  }

  onMounted(async () => {
    effort.value = await useGeneratedEffortStore()
      .fetchByIri('/api/efforts/' + data.value.effort)
      .then((d) => d.value)
    loading.value = false
  })

  // const emit = defineEmits<{ read: [Event]; close: [Event] }>()

  function getName(mode: 'RESPONSIBLE' | 'OWNER' | 'MEMBER'): string {
    switch (mode) {
      case 'RESPONSIBLE':
        return 'Verantwortliche/r'
      case 'OWNER':
        return 'Besitzer/in'
      case 'MEMBER':
        return 'Beteiligte/r'
    }
  }
</script>

<template>
  <div class="flex flex-row">
    <div class="flex-1">
      <PageLoading v-if="loading" />
      <div v-else>
        <span class="text-sm font-semibold leading-6">Sie wurden als {{ getName(data.mode) }} verknüpft:</span>
        <EffortLink
          v-if="effort"
          :id="effort.id"
          fetch-title
          show-id
          no-icon />
      </div>
    </div>
    <div class="flex-0 items-center md:hidden">
      <ButtonDefault
        outlined
        plain
        size="small"
        icon="MoreIcon"
        @click="toggleMenu" />
      <Menu
        ref="menu"
        :model="contextMenuItems"
        class="relative flex-none"
        popup>
        <template #itemicon="itm">
          <component
            :is="itm.item.icon"
            :class="[itm.class]" />
        </template>
      </Menu>
    </div>
  </div>
</template>
